import React, { createContext, useState, useContext, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut, signInWithCustomToken } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const auth = getAuth();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
            if (authUser) {
                try {
                    // User is signed in, get the user document from Firestore.
                    const userDocRef = doc(db, 'users', authUser.uid);
                    const userDocSnap = await getDoc(userDocRef);

                    if (userDocSnap.exists()) {
                        const userData = userDocSnap.data();

                        // Query for auctions the user is an admin of
                        const adminQuery = query(collection(db, 'auctions'), where('adminId', '==', authUser.uid));
                        const adminSnapshot = await getDocs(adminQuery);
                        const adminIn = adminSnapshot.docs.map(doc => doc.id);

                        // Query for auctions the user is a participant in
                        const participantQuery = query(collection(db, 'auctions'), where('participantUids', 'array-contains', authUser.uid));
                        const participantSnapshot = await getDocs(participantQuery);
                        const participantIn = participantSnapshot.docs.map(doc => doc.id);

                        // Combine auth data, firestore data, and auction memberships
                        setUser({ 
                            ...authUser, 
                            ...userData, 
                            adminIn, 
                            participantIn 
                        });
                    } else {
                        // This can happen if the user exists in Firebase Auth but not in Firestore
                        console.error("No user document found in Firestore for UID:", authUser.uid);
                        setUser(authUser); // Fallback to only auth user
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                    setUser(authUser); // Fallback to auth user on error
                } finally {
                    setLoading(false);
                }
            } else {
                // User is signed out
                setUser(null);
                setLoading(false);
            }
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [auth]);

    const logout = () => {
        signOut(auth);
    };

    /**
     * Herlaadt het Firestore-userdocument van de ingelogde gebruiker en voegt het
     * samen met de bestaande user-state. Nodig na een profielwijziging (bijv. een
     * nieuwe gebruikersnaam via de Accountpagina), zodat de header en pagina's de
     * nieuwe waarde tonen zonder een volledige herlaad-actie.
     */
    const refreshUserData = async () => {
        const authUser = auth.currentUser;
        if (!authUser) return;
        const userDocSnap = await getDoc(doc(db, 'users', authUser.uid));
        if (userDocSnap.exists()) {
            setUser((prev) => ({ ...prev, ...userDocSnap.data() }));
        }
    };

    /**
     * Logt in als een andere gebruiker (alleen voor veiling-admins).
     * @param {string} targetUid - De UID van de gebruiker om als in te loggen
     * @param {string} auctionId - De veiling ID
     */
    const impersonateUser = async (targetUid, auctionId) => {
        const createImpersonationToken = httpsCallable(functions, 'createImpersonationToken');
        const result = await createImpersonationToken({ targetUid, auctionId });
        const { token } = result.data;
        await signInWithCustomToken(auth, token);
    };

    // Globale administrator: beheert seizoenen, stamgegevens en puntentelling voor het
    // hele spel. Los van de subleaguebeheerder, die alleen zijn eigen veiling beheert
    // (auctions/{id}.adminId). Zie firestore.rules: isAdmin() vs isAuctionAdmin().
    const isGlobalAdmin = user?.role === 'admin';

    return (
        <AuthContext.Provider value={{ user, loading, logout, impersonateUser, isGlobalAdmin, refreshUserData }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);